import styled from "styled-components";
import React, { ReactNode, useState } from "react";
import { UILayer } from "../../CommonUI/BuildlingBlocks/UILayer";
import { PlusGrayIcon } from "../../CommonUI/Icon/PlusGrayIcon";
import { StyledButton } from "../../CommonUI/BuildlingBlocks/StyledButton";
import { StyledCheckBox } from "../../CommonUI/BuildlingBlocks/StyledCheckbox";
import { StyledFormControlLabel } from "../../CommonUI/BuildlingBlocks/StyledFormControlLabel";

import { GridCellRender, XGridArea, XGridRow } from "./XGridContainer";
import { FormContext } from "../Utilities/FormContext";
import { StyledLink } from "../../CommonUI/BuildlingBlocks/StyledLink";
import { ArrowBack, ArrowDownward, ArrowForward, ArrowUpward } from "@mui/icons-material";
import { ObserveState } from "../../CommonUI/StateManagement/ObserveState";
import { PropertyEditorInteger } from "../../CommonUI/PropertyEditing/PropertyEditorInteger";
import { StateManager } from "../../rule-engine-core/Common/StateManager";
import { PropertyEditorColor } from "../../CommonUI/PropertyEditing/PropertyEditorColor";
import { BorderStyle, FormDesignConstants } from "../Utilities/FormDesignConstants";
import { PropertyEditorSelect } from "../../CommonUI/PropertyEditing/PropertyEditorSelect";
import { ChildFullWidthDiv } from "../Controls/Web/CommonStyled";
import { ValidationIssue } from "../../rule-engine-core/Common/IDesignValidationProvider";
import { Box } from "@mui/material";
import { ValidationInformation } from "../../Rule/DesignUI/ValidationInformation";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";

export interface IGridArea {
    row: number;
    column: number;

    width: number;
    height: number;
}

export interface IGridContainer {
    id: string;
    areas: XGridArea[];
    rows: XGridRow[];

    rowsLength: number;
    columnsLength: number;

    innerMargin?: number;
    gapBetweenAreas: number;
    defaultAreaInnerMargin?: number;
    defaultShowAreaBorder?: boolean;
    defaultAreaBorderColor?: string;
    defaultAreaBorderRadius?: number;
    defaultAreaBorderWidth?: number;
    defaultAreaBorderStyle?: BorderStyle;
    renderEditUI: () => JSX.Element;
    toString: () => string | null;
    getInnerMargin: () => number | undefined;
    backgroundColor: () => string | undefined | null;

    getDesignValidationIssues(): ValidationIssue[];

    getFormContext(): FormContext | undefined;

    setFormContext(value: FormContext | undefined): void;

    addArea(area: XGridArea): void;

    removeArea(area: XGridArea): void;

    addRow(row: XGridRow): void;

    removeRow(row: XGridRow): void;
}

interface IGridEditLayer {
    grid: IGridContainer;
    updateCallback: () => void;
}

export const XGridEditLayer = (props: IGridEditLayer) => {
    const [grid, setGrid] = useState<IGridContainer>(props.grid);
    const [selected, setSelected] = useState<IGridArea | undefined>();
    const [edit, setEdit] = useState<IGridContainer | XGridRow | XGridArea | null | undefined>(null);

    let containedRealArea: XGridArea | null | undefined = selected
        ? grid.areas.find((area) => {
              return isContained(area, selected);
          })
        : null;

    let selectedOnlyReal = containedRealArea && selected && compareArea(containedRealArea!, selected!);

    const onAddNewArea = (area: IGridArea) => () => {
        let newArea = new XGridArea(area.row, area.column, area.width, area.height);
        props.grid.addArea(newArea);

        setGrid({
            ...grid,
            areas: props.grid.areas,
        });
        setSelected(area);
    };

    const onAddNewRow = () => {
        props.grid.addRow(new XGridRow());

        setGrid({
            ...grid,
            rows: props.grid.rows,
            rowsLength: props.grid.rowsLength,
        });
    };

    const onRowEdit = (rowIndex: number) => () => {
        setEdit(props.grid.rows[rowIndex]);
    };

    const onSelectArea = (area: IGridArea) => (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();

        setSelected({ ...area });
    };

    const onMergeArea = () => {
        if (!containedRealArea || !selected) return;

        props.grid.areas = grid.areas.map((area) => {
            if (compareArea(area, containedRealArea!)) {
                area.height = selected.height;
                area.width = selected.width;
                area.row = selected.row;
                area.column = selected.column;

                setEdit(area);
            }

            return area;
        });

        setGrid({
            ...grid,
            areas: props.grid.areas,
        });
    };

    const onExtendSelectedArea = (area: IGridArea) => () => {
        if (!selected) return;
        // adding boundary area
        if (
            isContained(area, {
                row: selected.row - 1,
                column: selected.column - 1,
                height: selected.height + 2,
                width: selected.width + 2,
            })
        ) {
            setSelected({
                row: Math.min(area.row, selected.row),
                column: Math.min(area.column, selected.column),
                height:
                    Math.max(selected.row + selected.height, area.row + area.height) - Math.min(area.row, selected.row),
                width:
                    Math.max(selected.column + selected.width, area.column + area.width) -
                    Math.min(area.column, selected.column),
            });
        }
    };

    const onRemoveArea = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();

        if (!containedRealArea || !selected) return;

        props.grid.removeArea(containedRealArea);

        setGrid({
            ...grid,
            areas: props.grid.areas,
        });
        setSelected(undefined);
    };

    const isSelectedArea = (area: IGridArea) => {
        if (!selected) return;

        return selected && isContained(area, selected!);
    };

    const onResizeArea = (size: { height?: number; width?: number; row?: number; column?: number }) => {
        let area = props.grid.areas.find((a) => a.id === Reflect.get(containedRealArea!, "id"));
        if (!area) {
            return;
        }
        let temp = selected!;
        if (size.row !== undefined) {
            area.row = size.row;
            temp.row = size.row;
        }
        if (size.column !== undefined) {
            area.column = size.column;
            temp.column = size.column;
        }
        if (size.height !== undefined) {
            area.height = size.height;
            temp.height = size.height;
        }
        if (size.width !== undefined) {
            area.width = size.width;
            temp.width = size.width;
        }

        setSelected({
            ...temp,
        });

        setGrid({
            ...grid,
            areas: props.grid.areas,
        });
    };

    let emptyAreaElems: JSX.Element[] = [];

    let rowIndex: number = 0;
    let columnIndex: number = 0;
    let sortedAreas = grid.areas;

    while (rowIndex < grid.rowsLength) {
        while (columnIndex < grid.columnsLength) {
            // eslint-disable-next-line
            let temp = sortedAreas.findIndex(function (area) {
                return area.row <= rowIndex && area.row + area.height > rowIndex && area.column === columnIndex;
            });
            if (temp >= 0) {
                columnIndex = columnIndex + sortedAreas[temp].width;

                if (columnIndex >= grid.columnsLength) {
                    columnIndex = 0;
                    rowIndex += 1;
                    break;
                }
            } else {
                const isSelected = isSelectedArea({
                    row: rowIndex,
                    column: columnIndex,
                    height: 1,
                    width: 1,
                });

                const isSelectedBoundaryArea =
                    selected &&
                    isContained(
                        { row: rowIndex, column: columnIndex, width: 1, height: 1 },
                        {
                            row: selected.row - 1,
                            column: selected.column - 1,
                            width: selected.width + 2,
                            height: selected.height + 2,
                        },
                    );

                const currentArea = {
                    row: rowIndex,
                    column: columnIndex,
                    height: 1,
                    width: 1,
                };

                emptyAreaElems.push(
                    <EmptyGridCellRender
                        rowStart={rowIndex}
                        columnStart={columnIndex}
                        selected={isSelected}
                        onClick={onSelectArea(currentArea)}
                        key={`${rowIndex}-${columnIndex}`}
                        data-testid={`XGridContainerEdit-empty-${rowIndex}-${columnIndex}`}
                    >
                        {(isSelected || (isSelectedBoundaryArea && containedRealArea)) && (
                            <CheckboxRender>
                                <StyledFormControlLabel
                                    control={
                                        <StyledCheckBox
                                            checked={isSelected}
                                            sx={{ background: UIStyleConstants.BLUE_COLOR }}
                                            name={`(${rowIndex}, ${columnIndex})`}
                                            onChange={onExtendSelectedArea(currentArea)}
                                        />
                                    }
                                    label=""
                                />
                            </CheckboxRender>
                        )}
                    </EmptyGridCellRender>,
                );
                columnIndex++;
                if (grid.columnsLength === columnIndex) {
                    columnIndex = 0;
                    rowIndex += 1;
                    break;
                }
            }
        }
    }

    const rowsTemplate = grid.rows
        .map((row) => (row.height ? `${row.height}px` : `minmax(75px, max-content)`))
        .join(" ");

    let editUI: ReactNode | undefined | null;

    if (edit instanceof XGridArea) {
        edit.onControlUpdate = () => {
            setSelected({
                row: edit.row,
                column: edit.column,
                width: edit.width,
                height: edit.height,
            });
            StateManager.changed(edit);
        };
        let editContext = edit.getFormContext();

        if (!editContext) return null;

        editContext.selectedId = edit.id;
        editUI = editContext.getEditUI() || null;
    } else if (edit instanceof XGridRow) {
        edit.onControlUpdate = () => {
            setGrid({
                ...grid,
                rows: [...props.grid.rows],
            });
            StateManager.changed(edit);
        };

        let editContext = edit.getFormContext();
        if (!editContext) return null;

        editContext.selectedId = edit.id;
        editUI = editContext.getEditUI() || null;
    } else {
        editUI = (
            <GridEditUI
                grid={props.grid}
                onUpdate={() => {
                    setGrid({ ...props.grid });
                }}
            />
        );
    }

    return (
        <UILayer
            padding="30px 0"
            open
            renderUI={() => (
                <XGridEditRender>
                    <EditUITitle>Edit Grid Container</EditUITitle>
                    <XGridEditContent>
                        <div style={{ overflow: "auto", flex: 1, padding: "0 30px" }}>
                            <ModeRender>
                                <span>select cell to add area </span>
                                {selected && (
                                    <StyledLink
                                        onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
                                            event.stopPropagation();
                                            event.preventDefault();

                                            setEdit(props.grid);
                                        }}
                                        size="small"
                                    >
                                        Edit Default Config
                                    </StyledLink>
                                )}
                            </ModeRender>
                            <GridContainer
                                rowsTemplate={rowsTemplate}
                                columnsLength={grid.columnsLength}
                                gapBetweenAreas={grid.gapBetweenAreas}
                                padding={props.grid.getInnerMargin()}
                                background={props.grid.backgroundColor()}
                            >
                                <Row
                                    grid={grid}
                                    onEdit={onRowEdit}
                                    actionRequired={true}
                                />
                                {emptyAreaElems}
                                {selected && (
                                    <SelectedGridCell
                                        rowStart={selected.row}
                                        columnStart={selected.column}
                                        width={selected.width}
                                        height={selected.height}
                                    >
                                        <ActionsRender position={"bottom"}>
                                            {selectedOnlyReal ? (
                                                <StyledButton
                                                    sx={{ background: UIStyleConstants.BLUE_COLOR }}
                                                    variant="contained"
                                                    onClick={onRemoveArea}
                                                    size="small"
                                                >
                                                    delete
                                                </StyledButton>
                                            ) : (
                                                <>
                                                    <StyledLink
                                                        onClick={() => {
                                                            setSelected(undefined);
                                                        }}
                                                        size="small"
                                                    >
                                                        Cancel
                                                    </StyledLink>
                                                    {containedRealArea ? (
                                                        <StyledButton
                                                            sx={{ background: UIStyleConstants.BLUE_COLOR }}
                                                            variant="contained"
                                                            onClick={onMergeArea}
                                                            size="small"
                                                        >
                                                            merge
                                                        </StyledButton>
                                                    ) : (
                                                        <StyledButton
                                                            sx={{ background: UIStyleConstants.BLUE_COLOR }}
                                                            variant="contained"
                                                            onClick={onAddNewArea({
                                                                row: selected.row,
                                                                column: selected.column,
                                                                width: selected.width,
                                                                height: selected.height,
                                                            })}
                                                            size="small"
                                                        >
                                                            Add
                                                        </StyledButton>
                                                    )}
                                                </>
                                            )}
                                        </ActionsRender>
                                        {selectedOnlyReal && (
                                            <>
                                                <SizeRender
                                                    position="top"
                                                    className="SizeRender"
                                                >
                                                    {(containedRealArea?.row || 0) > 0 && (
                                                        <IconButton
                                                            onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                                                event.stopPropagation();

                                                                onResizeArea({
                                                                    row: (containedRealArea?.row || 1) - 1,
                                                                    height: (containedRealArea?.height || 0) + 1,
                                                                });
                                                            }}
                                                        >
                                                            <ArrowUpward />
                                                        </IconButton>
                                                    )}
                                                    {(containedRealArea?.height || 0) > 1 && (
                                                        <IconButton
                                                            onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                                                event.stopPropagation();

                                                                onResizeArea({
                                                                    row: (containedRealArea?.row || 0) + 1,
                                                                    height: (containedRealArea?.height || 1) - 1,
                                                                });
                                                            }}
                                                        >
                                                            <ArrowDownward />
                                                        </IconButton>
                                                    )}
                                                </SizeRender>
                                                <SizeRender position="bottom">
                                                    {(containedRealArea?.height || 0) > 1 && (
                                                        <IconButton
                                                            onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                                                event.stopPropagation();

                                                                onResizeArea({
                                                                    height: (containedRealArea?.height || 0) - 1,
                                                                });
                                                            }}
                                                        >
                                                            <ArrowUpward />
                                                        </IconButton>
                                                    )}
                                                    {(containedRealArea?.row || 0) + (containedRealArea?.height || 0) <
                                                        grid.rowsLength && (
                                                        <IconButton
                                                            onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                                                event.stopPropagation();

                                                                onResizeArea({
                                                                    height: (containedRealArea?.height || 0) + 1,
                                                                });
                                                            }}
                                                        >
                                                            <ArrowDownward />
                                                        </IconButton>
                                                    )}
                                                </SizeRender>
                                                <SizeRender position="right">
                                                    {(containedRealArea?.width || 0) > 1 && (
                                                        <IconButton
                                                            onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                                                event.stopPropagation();

                                                                onResizeArea({
                                                                    width: (containedRealArea?.width || 0) - 1,
                                                                });
                                                            }}
                                                        >
                                                            <ArrowBack />
                                                        </IconButton>
                                                    )}
                                                    {(containedRealArea?.column || 0) +
                                                        (containedRealArea?.width || 0) <
                                                        grid.columnsLength && (
                                                        <IconButton
                                                            onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                                                event.stopPropagation();

                                                                onResizeArea({
                                                                    width: (containedRealArea?.width || 0) + 1,
                                                                });
                                                            }}
                                                        >
                                                            <ArrowForward />
                                                        </IconButton>
                                                    )}
                                                </SizeRender>
                                                <SizeRender position="left">
                                                    {(containedRealArea?.column || 0) > 0 && (
                                                        <IconButton
                                                            onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                                                event.stopPropagation();

                                                                onResizeArea({
                                                                    width: (containedRealArea?.width || 0) + 1,
                                                                    column: (containedRealArea?.column || 0) - 1,
                                                                });
                                                            }}
                                                        >
                                                            <ArrowBack />
                                                        </IconButton>
                                                    )}
                                                    {(containedRealArea?.width || 0) > 1 && (
                                                        <IconButton
                                                            onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                                                event.stopPropagation();

                                                                onResizeArea({
                                                                    width: (containedRealArea?.width || 0) - 1,
                                                                    column: (containedRealArea?.column || 0) + 1,
                                                                });
                                                            }}
                                                        >
                                                            <ArrowForward />
                                                        </IconButton>
                                                    )}
                                                </SizeRender>
                                            </>
                                        )}
                                    </SelectedGridCell>
                                )}
                                {sortedAreas.map((area, index) => (
                                    <RealArea
                                        area={area}
                                        onSelectArea={onSelectArea}
                                        key={index}
                                        onClick={() => {
                                            setEdit(area);
                                            setSelected({
                                                row: area.row,
                                                column: area.column,
                                                height: area.height,
                                                width: area.width,
                                            });
                                        }}
                                    />
                                ))}

                                <NewRow
                                    grid={grid}
                                    onAdd={onAddNewRow}
                                />
                            </GridContainer>
                        </div>
                        <ObserveState
                            listenTo={edit || props.grid}
                            control={() => {
                                return (
                                    <EditUI>
                                        <EditUITitle>Edit {(edit || props.grid).toString()}</EditUITitle>
                                        <EditUIContent>
                                            <ChildFullWidthDiv>{editUI}</ChildFullWidthDiv>
                                        </EditUIContent>
                                    </EditUI>
                                );
                            }}
                        />
                    </XGridEditContent>

                    <Box
                        display="flex"
                        justifyContent="space-between"
                        padding="0 15px"
                    >
                        <span>
                            <ValidationInformation provider={props.grid} />
                        </span>
                        <DialogActions>
                            <StyledButton
                                sx={{ background: UIStyleConstants.BLUE_COLOR }}
                                variant="contained"
                                onClick={props.updateCallback}
                            >
                                Done
                            </StyledButton>
                        </DialogActions>
                    </Box>
                </XGridEditRender>
            )}
            showCloseButton
            onClose={() => props.updateCallback()}
        />
    );
};

const RowRender = styled.div<{ rowStart: number; columnsLength: number; enabledActions: boolean }>`
    grid-column-start: 1;
    grid-column-end: ${(props) => props.columnsLength + 1};

    grid-row-start: ${(props) => props.rowStart + 1};
    grid-row-end: ${(props) => props.rowStart + 2};

    position: relative;

    ${(props) =>
        props.enabledActions
            ? `
                &:hover {

                    &::before {
                        content: "";
                        position: absolute;
                        top: -4px;
                        right: -4px;
                        left: -4px;
                        bottom: -4px;
                        border: 2px solid ${UIStyleConstants.BLUE_GRAY_COLOR};
                        border-radius: 6px;
                    }
                }
            `
            : ``}
`;

const ModeRender = styled.div`
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 32px;

    display: flex;
    justify-content: space-between;
    align-items: center;

    > span {
        margin-right: 10px;
    }
`;

const Row = ({
    onEdit,
    grid,
    actionRequired,
}: {
    onEdit: (rowIndex: number) => () => void;
    grid: IGridContainer;
    actionRequired: boolean;
}) => {
    let emptyAreaElems: JSX.Element[] = [];

    //  adding add new row columns;
    for (let i = 0; i < grid.rowsLength; i++) {
        emptyAreaElems.push(
            <RowRender
                rowStart={i}
                columnsLength={grid.columnsLength}
                key={i}
                enabledActions={actionRequired}
                onClick={onEdit(i)}
            />,
        );
    }

    return <>{emptyAreaElems}</>;
};

const NewRow = ({ onAdd, grid }: { onAdd: () => void; grid: IGridContainer }) => {
    let emptyAreaElems: JSX.Element[] = [];
    // adding row for property editor
    for (let i = 0; i < grid.columnsLength; i++) {
        emptyAreaElems.push(
            <EmptyGridCellRender
                rowStart={grid.rowsLength}
                columnStart={i}
                addRowColumn
                onClick={onAdd}
                firstColumn={i === 0}
                key={i}
                data-testid={`XGridContainerEdit-newRow-${i}`}
            >
                {i === 0 && <PlusGrayIcon />}
            </EmptyGridCellRender>,
        );
    }

    return <>{emptyAreaElems}</>;
};

const RealArea = ({
    area,
    onSelectArea,
    onClick,
}: {
    area: XGridArea;
    onSelectArea: (area: IGridArea) => (event: React.MouseEvent<HTMLDivElement>) => void;
    onClick: () => void;
}) => {
    return (
        <GridCellRender
            rowStart={area.row}
            columnStart={area.column}
            columnSpan={area.width}
            rowSpan={area.height}
            onClick={onSelectArea(area)}
            backgroundColor={area.backgroundColor()}
            borderColor={area.borderColor()}
            borderStyle={area.borderStyle()}
            borderWidth={area.borderWidth()}
            borderRadius={area.borderRadius()}
            padding={area.getInnerMargin()}
            data-testid={`XGridContainerEdit-${area.row}-${area.column}`}
        >
            <GridCellContentRender
                onClick={onClick}
                data-testid={`${area.row}-${area.column}-editCell`}
            >
                Click to Edit
            </GridCellContentRender>
        </GridCellRender>
    );
};

const compareArea = (area1: IGridArea, area2: IGridArea): boolean => {
    let area1Id = Reflect.get(area1, "id");
    let area2Id = Reflect.get(area2, "id");

    if (area1Id && area2Id) {
        return area1Id === area2Id;
    }

    return (
        area1.row === area2.row &&
        area1.column === area2.column &&
        area1.height === area2.height &&
        area1.width === area2.width
    );
};

const isContained = (area: IGridArea, included: IGridArea): boolean => {
    return (
        area.row >= included.row &&
        area.row + area.height <= included.row + included.height &&
        area.column >= included.column &&
        area.column + area.width <= included.column + included.width
    );
};

const DialogActions = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;

    > * {
        margin-left: 10px !important;

        &:first-child {
            margin-left: 0 !important;
        }
    }
`;

const XGridEditRender = styled.div`
    height: 80vh;
    width: 80vw;

    display: flex;
    flex-direction: column;

    background-color: ${UIStyleConstants.BLUE_VERY_LIGHT_COLOR};
`;

const GridContainer = styled.div<{
    rowsTemplate: string;
    columnsLength: number;
    gapBetweenAreas: number;
    padding?: number;
    background?: string | null;
}>`
    display: grid;

    position: relative;

    grid-template-columns: repeat(${(props) => props.columnsLength}, 1fr);
    grid-template-rows: ${(props) => props.rowsTemplate};

    column-gap: ${(props) => props.gapBetweenAreas}px;
    row-gap: ${(props) => props.gapBetweenAreas}px;

    ${(props) => (props.padding !== undefined ? `padding: ${props.padding}px;` : ``)}

    ${(props) => (props.background ? `background: ${props.background};` : ``)}

  width: 800px;
`;

const EmptyGridCellRender = styled.div<{
    rowStart: number;
    columnStart: number;
    addRowColumn?: boolean;
    firstColumn?: boolean;
    selected?: boolean;
}>`
    display: flex;
    align-items: center;
    justify-content: center;

    grid-column: ${(props) => props.columnStart + 1} / span 1;
    grid-row: ${(props) => props.rowStart + 1} / span 1;

    position: relative;
    border-radius: 4px;

    ${(props) =>
        props.addRowColumn
            ? `
                height: 60px;
            `
            : `
                height: 100%;
                min-height: 75px;
            `};

    ${(props) =>
        props.addRowColumn || props.selected
            ? `
                        background-color: ${
                            props.firstColumn
                                ? UIStyleConstants.BLUE_VERY_LIGHT_COLOR
                                : UIStyleConstants.BLUE_VERY_LIGHT_COLOR
                        }); 
                    `
            : `
                        border: 1px dashed  ${UIStyleConstants.GRAY_COLOR}; 
                    `};

    ${(props) =>
        props.selected
            ? `
                &::after {
                    content: "";
                    border-radius: 8px;
                    position: absolute;
                    top: 4px;
                    bottom: 4px;
                    right: 4px;
                    left: 4px;
                    border: 1px dashed  ${UIStyleConstants.GRAY_COLOR}; 
                }
            `
            : ``}
`;

const GridCellContentRender = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;

    cursor: pointer;

    height: 100%;

    border: 1px dashed ${UIStyleConstants.GRAY_LIGHTER_COLOR};
    border-radius: 4px;

    //styleName: text/Inter Medium 11px;

    font-size: 11px;
    font-style: normal;
    font-weight: 500;
    line-height: 15px;
    letter-spacing: 0em;
    text-align: center;

    color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
`;

const SelectedGridCell = styled.div<{
    rowStart: number;
    columnStart: number;
    width: number;
    height: number;
}>`
    grid-column-start: ${(props) => props.columnStart + 1};
    grid-column-end: ${(props) => props.columnStart + 1 + props.width};

    grid-row-start: ${(props) => props.rowStart + 1};
    grid-row-end: ${(props) => props.rowStart + 1 + props.height};

    position: relative;

    &::before {
        content: "";
        position: absolute;
        top: -4px;
        bottom: -4px;
        left: -4px;
        right: -4px;

        border-radius: 6px;
        border: 2px solid ${UIStyleConstants.BLUE_GRAY_COLOR};
    }
`;

const ActionsRender = styled.div<{ position?: "top" | "bottom" }>`
    position: absolute;

    align-items: center;
    z-index: 2;

    ${(props) =>
        props.position === "bottom"
            ? `
                top: calc(100% + 10px);
                right: 0px;
                padding: 5px;

                & > * {
                    margin-left: 5px !important;

                    &:first-child {
                        margin-left: 0;
                    }
                }
            `
            : `
                bottom: 100%;
                right: 4px;
            `}

    border-radius: 4px;
    background: ${UIStyleConstants.WHITE_COLOR};
    border: 1px solid ${UIStyleConstants.GRAY_VERY_LIGHT_COLOR};
    box-sizing: border-box;
    border-radius: 4px;

    display: flex;
`;

const CheckboxRender = styled.div`
    position: absolute;
    right: -5px;
    top: 3px;
`;

const SizeRender = styled.div<{
    position: "top" | "bottom" | "left" | "right";
}>`
    position: absolute;

    display: flex;

    z-index: 2;

    ${(props) =>
        props.position === "top"
            ? `top: 5px;left: 50%; transform: translateX(-50%); flex-direction: row;`
            : props.position === "bottom"
              ? `bottom: 5px;left: 50%; transform: translateX(-50%); flex-direction: row;`
              : props.position === "right"
                ? `top: 50%;right: 5px; transform: translateY(-50%); flex-direction: column;`
                : `top: 50%;left: 5px; transform: translateY(-50%);  flex-direction: column;`}
`;

const IconButton = styled.div`
    width: 16px;
    height: 16px;

    display: flex;
    align-items: center;
    justify-content: center;

    cursor: pointer;

    opacity: 0.7;

    &:hover {
        opacity: 1;
    }

    .MuiSvgIcon-root {
        width: 12px;
        height: 12px;
    }
`;

const EditUI = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;
    width: 280px;
    background: ${UIStyleConstants.WHITE_COLOR};

    > * {
        margin-bottom: 10px !important;
    }
`;

const EditUITitle = styled.div`
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 16px;
    letter-spacing: 0em;
    text-align: left;
    padding: 10px 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    margin-bottom: 0;

    .close {
        background: ${UIStyleConstants.GRAY_VERY_LIGHT_COLOR};
    }
`;

const EditUIContent = styled.div`
    padding: 10px 15px;
    overflow: auto;
    flex: 1;

    > * {
        margin-bottom: 15px !important;
    }
`;

const GridEditUI = (props: { grid: IGridContainer; onUpdate: () => void }) => {
    const updateControl = () => {
        props.onUpdate();
        StateManager.changed(props.grid);
    };

    return (
        <>
            <PropertyEditorColor
                editObject={props.grid}
                propertyName="containerBackgroundColor"
                label="Container Background Color"
                onChange={updateControl}
            />
            <PropertyEditorInteger
                editObject={props.grid}
                propertyName="gapBetweenAreas"
                label="Gap Between Areas"
                onChange={updateControl}
            />
            <PropertyEditorInteger
                editObject={props.grid}
                propertyName="innerMargin"
                label="Inner Margin"
                onChange={updateControl}
            />

            <PropertyEditorColor
                editObject={props.grid}
                propertyName="defaultAreaBackgroundColor"
                label="Default Area Background Color"
                onChange={updateControl}
            />

            <PropertyEditorInteger
                editObject={props.grid}
                label="Default Area Border Width"
                propertyName="defaultAreaBorderWidth"
                onChange={updateControl}
            />
            <PropertyEditorInteger
                editObject={props.grid}
                label="Default Area Border Radius"
                propertyName="defaultAreaBorderRadius"
                onChange={updateControl}
            />

            <PropertyEditorColor
                editObject={props.grid}
                label="Default Area Border Color"
                propertyName="defaultAreaBorderColor"
                onChange={updateControl}
            />
            <PropertyEditorSelect
                editObject={props.grid}
                label="Default Area Border Style"
                propertyName="defaultAreaBorderStyle"
                onChange={updateControl}
                options={FormDesignConstants.BORDER_STYLES}
            />

            <PropertyEditorInteger
                editObject={props.grid}
                label="Default Area Inner Margin"
                propertyName="defaultAreaInnerMargin"
                onChange={updateControl}
            />
        </>
    );
};

export const XGridEditContent = styled.div`
    display: flex;
    flex-direction: row;
    flex: 1;
    overflow: auto;
`;
